import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MainLayout from '../../../shared/components/MainLayout';
import api from '../../../shared/utils/api';

// 🔷 Card-ийн props төрөл
type CardProps = {
    title: string;
    desc: string;
    icon: string;
    onClick?: () => void;
};

// 🔷 Statistics Card props
type StatsCardProps = {
    title: string;
    value: number;
    icon: string;
    color: string;
};

// ✅ Карт компонент
function Card({ title, desc, icon, onClick }: CardProps) {
    return (
        <div 
            className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition cursor-pointer"
            onClick={onClick}
        >
            <div className="text-3xl mb-4">{icon}</div>
            <h3 className="font-semibold text-lg mb-2">{title}</h3>
            <p className="text-sm text-gray-600">{desc}</p>
        </div>
    );
}

// ✅ Statistics Card компонент
function StatsCard({ title, value, icon, color, onView }: StatsCardProps & { onView?: () => void }) {
    return (
        <div className={`bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 border-l-4 ${color}`}>
            <div className="flex items-center justify-between mb-4">
                <div>
                    <p className="text-sm font-medium text-gray-600">{title}</p>
                    <p className="text-3xl font-bold text-gray-900">{value.toLocaleString()}</p>
                </div>
                <div className="text-4xl">{icon}</div>
            </div>
            {onView && (
                <button
                    onClick={onView}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg transition-colors duration-200 font-medium"
                >
                    Харах
                </button>
            )}
        </div>
    );
}

// ✅ Карт мэдээлэл
const adminCards: CardProps[] = [
    {
        title: 'Хэрэглэгчдийн удирдлага',
        desc: 'Системийн хэрэглэгчдийг нэмэх, засах, устгах болон эрхийг удирдах.',
        icon: '👥',
    },
    {
        title: 'Харилцагчдын удирдлага',
        desc: 'Харилцагчдын мэдээллийг харах, нэмэх, засах, устгах.',
        icon: '🏢',
    },
    {
        title: 'Бизнес ангиллын удирдлага',
        desc: 'Бизнес ангиллын жагсаалтыг харах, нэмэх, засах.',
        icon: '📋',
    },
    {
        title: 'Системийн тохиргоо',
        desc: 'Системийн ерөнхий тохиргоо, хэрэглэгчийн эрх, аюулгүй байдлын тохиргоо.',
        icon: '⚙️',
    },
    {
        title: 'Тайлан',
        desc: 'Системийн ерөнхий тайлан, статистик мэдээлэл, ашиглалтын дүн.',
        icon: '📊',
    },
    {
        title: 'Нууц үг сэргээх',
        desc: 'Хэрэглэгчдийн нууц үгийг сэргээх, шинэчлэх боломж.',
        icon: '🔐',
    },
];

// ✅ Гол компонент
export default function AdminMainPage() {
    const navigate = useNavigate();
    const location = useLocation();

    // Statistics state
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalCustomers: 0,
        totalAccountants: 0
    });

    useEffect(() => {
        // Fetch statistics when component mounts
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const result = await api.getAdminStats();
            if (result.success) {
                setStats(result.data);
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const handleCardClick = (title: string) => {
        switch (title) {
            case 'Хэрэглэгчдийн удирдлага':
                navigate('/HomeScreen_AdminPanel/hariltsagch');
                break;
            case 'Харилцагчдын удирдлага':
                navigate('/HomeScreen_AdminPanel/hariltsagch');
                break;
            case 'Бизнес ангиллын удирдлага':
                navigate('/HomeScreen_AdminPanel/business-categories');
                break;
            case 'Системийн тохиргоо':
                navigate('/HomeScreen_AdminPanel/settings');
                break;
            case 'Тайлан':
                navigate('/HomeScreen_AdminPanel/tushaah-tailan');
                break;
            case 'Нууц үг сэргээх':
                navigate('/HomeScreen_AdminPanel/password-recovery');
                break;
            default:
                console.log('Unknown card clicked:', title);
        }
    };

    const handleViewUsers = () => {
        navigate('/HomeScreen_AdminPanel/app-user');
    };

    const handleViewCustomers = () => {
        navigate('/HomeScreen_AdminPanel/hariltsagch');
    };

    const handleViewAccountants = () => {
        navigate('/HomeScreen_AdminPanel/accountant');
    };

    return (
        <MainLayout>
            <div className="flex flex-col items-center justify-center min-h-[80vh] w-full">
                
                <h1 className="text-3xl font-bold text-center mb-2">Админ удирдлага</h1>
                <p className="text-center text-lg mb-2">Системийн бүх хэрэглэгч, харилцагч, тохиргоог удирдах боломжтой админ хэсэгт тавтай морил.</p>
                <p className="text-center text-base mb-8">Нэвтэрсэн хэрэглэгч: SystemAdmin</p>
                
                {/* Statistics Section */}
                <div className="w-full max-w-6xl mx-auto mb-12">
                    <h2 className="text-2xl font-bold mb-6 text-center">Системийн статистик</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <StatsCard
                            title="Нийт хэрэглэгч"
                            value={stats.totalUsers}
                            icon="👥"
                            color="border-blue-500"
                            onView={handleViewUsers}
                        />
                        <StatsCard
                            title="Нийт харилцагч"
                            value={stats.totalCustomers}
                            icon="🏢"
                            color="border-green-500"
                            onView={handleViewCustomers}
                        />
                        <StatsCard
                            title="Нийт нягтлан бодогч"
                            value={stats.totalAccountants}
                            icon="📊"
                            color="border-purple-500"
                            onView={handleViewAccountants}
                        />
                    </div>
                </div>

                {/* Management Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 w-full max-w-5xl mx-auto">
                    {adminCards.map((card) => (
                        <Card key={card.title} {...card} onClick={() => handleCardClick(card.title)} />
                    ))}
                </div>
            </div>
        </MainLayout>
    );
} 